import React, { useContext, useRef, useEffect, forwardRef } from 'react';
import { createSurveyStyles } from '@sm/webassets';
import clsx from 'classnames';
import { SurveyThemeType } from '@sm/webassets/dist/components/SurveyTheme';
import Context from './Context';
import useOnFocusedRef from './hooks/useOnFocusedRef';
import useFocusOnActive from './hooks/useFocusOnActive';
import useForkRef from './hooks/useForkRef';

const useStyles = createSurveyStyles<'shield', { active: boolean; noShield: boolean }>((theme: SurveyThemeType) => ({
  shield: {
    opacity: ({ active, noShield }) => (!!noShield || active ? 1 : 0.5),
    paddingBottom: '80px',
    [`@media (max-width: ${theme.breakpoints?.xs.max})`]: {
      paddingBottom: '270px',
    },
  },
}));

export type ScrollAnchorRenderProps = {
  isActive: boolean;
  nextQuestion(e: React.MouseEvent<Element, MouseEvent>): void;
};

export type Props = React.PropsWithChildren<
  {
    noShield?: boolean;
  } & React.HTMLAttributes<HTMLDivElement>
>;

function ScrollAnchor(
  { id, noShield = false, children, className, ...props }: Props,
  ref?: React.Ref<HTMLDivElement>
): React.ReactElement {
  const anchorRef = useRef<HTMLDivElement>(null);
  const handleRef = useForkRef<HTMLDivElement>(anchorRef, ref);
  const { register, unregister, activeAnchor, scrollTo, nextQuestion } = useContext(Context);

  const isActive = useFocusOnActive(anchorRef, activeAnchor);
  useOnFocusedRef(anchorRef, scrollTo);

  const { shield } = useStyles({ active: isActive, noShield });

  const handleShieldClick = (): void => {
    scrollTo(anchorRef);
  };

  let content = children;
  if (typeof children === 'function') {
    content = children({ isActive, nextQuestion });
  }
  useEffect(() => {
    register(anchorRef);
    return () => unregister(anchorRef);
  }, [ref, register, unregister]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      id={id}
      ref={handleRef}
      className={clsx(shield, className)}
      onClick={handleShieldClick}
      tabIndex={-1}
      {...props}
    >
      {content}
    </div>
  );
}

export default forwardRef<HTMLDivElement, Props>(ScrollAnchor);
